import React, { FunctionComponent, useEffect, useState } from 'react';
import { Button, Chip, IconButton } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import ConfirmDialog from 'src/components/ConfirmDialog';
import AddDatadogCredentialModal, {
    IDatadogCredentialValues,
} from 'src/components/AddDatadogCredentialModal';
import {
    IDatadogTestConnectionResult,
    IDatadogValidateRequest,
    IWorkspaceSetting,
} from 'src/hooks/useWorkspaceSettings';

export interface IDatadogCredentialsSectionProps {
    workspaceId: string
    settingDefinitions: IWorkspaceSetting[]
    settingValues: { [name: string]: string | null }
    setSettingValues: React.Dispatch<React.SetStateAction<{ [name: string]: string | null }>>
    validateDatadogCredential: (workspaceId: string, body: IDatadogValidateRequest) => Promise<IDatadogTestConnectionResult>
    testDatadogConnection: (workspaceId: string) => Promise<IDatadogTestConnectionResult>
    saveSuccessSignal?: string | null
}

const DATADOG_KEYS = ['datadog.api_key', 'datadog.site', 'datadog.service', 'datadog.enabled'] as const

const DatadogCredentialsSection: FunctionComponent<IDatadogCredentialsSectionProps> = ({
    workspaceId,
    settingDefinitions,
    settingValues,
    setSettingValues,
    validateDatadogCredential,
    saveSuccessSignal,
}) => {
    const apiKeyDef = settingDefinitions.find(s => s.name === 'datadog.api_key')
    const siteDef = settingDefinitions.find(s => s.name === 'datadog.site')
    const serviceDef = settingDefinitions.find(s => s.name === 'datadog.service')
    const enabledDef = settingDefinitions.find(s => s.name === 'datadog.enabled')

    // settingValues['datadog.api_key'] tri-state:
    //   undefined → not staged, defer to server
    //   null      → staged removal (disconnect dialog)
    //   string    → staged save (modal)
    const stagedApiKey = settingValues['datadog.api_key']
    const isConfigured = stagedApiKey === undefined
        ? (apiKeyDef?.configured ?? false)
        : stagedApiKey !== null
    const valueHint = apiKeyDef?.valueHint
    const stagedSite = settingValues['datadog.site']
    const site = stagedSite !== undefined ? stagedSite : (apiKeyDef?.configured ? siteDef?.value : null) || siteDef?.defaultValue || 'EU1'

    const stagedEnabled = settingValues['datadog.enabled']
    const enabledRaw = stagedEnabled !== undefined ? stagedEnabled : (enabledDef?.value ?? enabledDef?.defaultValue ?? 'true')
    const isEnabled = enabledRaw !== 'false'

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isDisconnectOpen, setIsDisconnectOpen] = useState(false)
    const [showStagedBanner, setShowStagedBanner] = useState(false)

    useEffect(() => {
        if (saveSuccessSignal) setShowStagedBanner(false)
    }, [saveSuccessSignal])

    // The validate endpoint only needs apiKey + site (service is a tag we set when saving,
    // Datadog's /api/v1/validate doesn't take it).
    const handleValidate = (values: IDatadogCredentialValues) =>
        validateDatadogCredential(workspaceId, { apiKey: values.apiKey, site: values.site })

    const handleSaved = (values: IDatadogCredentialValues) => {
        setSettingValues(prev => ({
            ...prev,
            'datadog.api_key': values.apiKey,
            'datadog.site': values.site,
            'datadog.service': values.service,
            'datadog.enabled': 'true',
        }))
        setShowStagedBanner(true)
    }

    const handleTogglePause = () => {
        setSettingValues(prev => ({
            ...prev,
            'datadog.enabled': isEnabled ? 'false' : 'true',
        }))
    }

    const handleDisconnectConfirm = async () => {
        setSettingValues(prev => {
            const next = { ...prev }
            for (const key of DATADOG_KEYS) next[key] = null
            return next
        })
        setShowStagedBanner(false)
        setIsDisconnectOpen(false)
    }

    // No definitions loaded yet (or backend doesn't expose datadog catalogue): render nothing.
    if (!apiKeyDef) return null

    return (
        <div
            data-testid="datadog-credentials-section"
            style={{
                margin: '25px auto',
                width: '100%',
                padding: '0 5px 0 0',
            }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 500 }}>
                Advanced Settings
            </h2>
            <div>
                <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img
                        data-testid="datadog-brand-icon"
                        src={`${process.env.PUBLIC_URL}/assets/logos/extractors/datadog.png`}
                        alt=""
                        aria-hidden="true"
                        style={{ width: 18, height: 18, objectFit: 'contain' }} />
                    Datadog
                </h3>
            {!isConfigured && (
                <div
                    data-testid="datadog-empty-card"
                    style={{
                        padding: 16,
                        backgroundColor: '#fafafa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 16,
                        width: '100%',
                    }}>
                    <p style={{ margin: 0, maxWidth: 360 }}>
                        There&apos;s no credential present for Datadog. Add a credential to get the Datadog service up and running.
                    </p>
                    <Button
                        data-testid="datadog-add-credential"
                        variant="outlined"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => setIsModalOpen(true)}>
                        Add credential
                    </Button>
                </div>
            )}
            {isConfigured && (
                <div data-testid="datadog-configured-card" style={{ width: '100%' }}>
                    <div
                        style={{
                            padding: 16,
                            border: '1px solid #ddd',
                            borderRadius: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8,
                        }}>
                        <span data-testid="datadog-credential-chip">
                            {valueHint ? `...${valueHint}` : 'Credential set'} &bull; {site}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Chip
                                data-testid="datadog-status-pill"
                                clickable
                                onClick={handleTogglePause}
                                label={isEnabled ? 'Active' : 'Paused'}
                                style={{
                                    backgroundColor: isEnabled ? '#4caf50' : '#9e9e9e',
                                    color: '#fff',
                                }} />
                            <IconButton
                                data-testid="datadog-disconnect"
                                size="small"
                                aria-label="Remove Datadog credential"
                                onClick={() => setIsDisconnectOpen(true)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </div>
                    </div>
                    {showStagedBanner && (
                        <Alert
                            data-testid="datadog-staged-banner"
                            severity="success"
                            style={{ marginTop: 12 }}>
                            Credential is tested successfully. Click the &apos;Save&apos; button to save the credentials.
                        </Alert>
                    )}
                </div>
            )}
            </div>
            <AddDatadogCredentialModal
                isOpen={isModalOpen}
                apiKeyDefinition={apiKeyDef}
                siteDefinition={siteDef}
                serviceDefinition={serviceDef}
                handleClose={() => setIsModalOpen(false)}
                handleValidate={handleValidate}
                handleSaved={handleSaved}
            />
            <ConfirmDialog
                isOpen={isDisconnectOpen}
                title="Remove Datadog credentials?"
                message="Pipeline logs will stop being forwarded to Datadog. ElasticSearch is unaffected and will continue to receive logs."
                cancelLabel="Cancel"
                confirmLabel="Remove"
                handleClose={() => setIsDisconnectOpen(false)}
                handleConfirm={handleDisconnectConfirm}
            />
        </div>
    )
}

export default DatadogCredentialsSection
